import { motion } from "framer-motion";

type SlideProps = {
  index: number;
  total: number;
  title: string;
  subtitle: string;
  text: string;
  image?: string;
};

const SlideSection = ({
  index,
  total,
  title,
  subtitle,
  text,
  image,
}: SlideProps) => {
  const panelStyle = {
    borderColor: "var(--surface-border)",
    background:
      "linear-gradient(160deg, color-mix(in srgb, var(--surface-strong) 12%, transparent), color-mix(in srgb, var(--surface) 6%, transparent))",
  };
  const imageFrameStyle = {
    borderColor: "var(--surface-border)",
    background: "color-mix(in srgb, black 20%, transparent)",
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.6 }}
      className="grid w-full max-w-6xl items-start gap-5 py-4 sm:gap-8 sm:py-12 lg:grid-cols-[1.1fr,0.9fr] lg:items-center lg:gap-12"
    >
      <motion.div
        initial={{ opacity: 0, x: -60 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="order-2 space-y-4 rounded-[1.5rem] border p-5 text-left shadow-[0_22px_60px_rgba(0,0,0,0.2)] backdrop-blur-xl sm:space-y-5 sm:rounded-[2rem] sm:p-8 md:text-left lg:order-1 lg:p-10"
        style={panelStyle}
      >
        <div className="flex items-center justify-between gap-3 md:justify-start">
          <span
            className="rounded-full border px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.22em] text-[color:var(--accent)] sm:text-xs sm:tracking-[0.28em]"
            style={{
              borderColor: "color-mix(in srgb, var(--accent) 35%, transparent)",
              background: "color-mix(in srgb, var(--accent) 12%, transparent)",
            }}
          >
            Slide {index + 1}
          </span>
          <span className="text-xs text-[color:var(--text-soft)] sm:text-sm">
            0{index + 1} / 0{total}
          </span>
        </div>
        <h1 className="text-[1.75rem] font-semibold leading-[1.08] text-[color:var(--text-strong)] sm:text-4xl md:text-5xl">
          {title}
        </h1>
        <h2 className="text-xs font-semibold uppercase tracking-[0.24em] text-[color:var(--accent)] sm:text-lg sm:tracking-[0.18em]">
          {subtitle}
        </h2>
        <p className="text-sm leading-7 text-[color:var(--text-muted)] sm:text-base sm:leading-8 md:text-lg">
          {text}
        </p>
      </motion.div>

      {image && (
        <motion.div
          initial={{ opacity: 0, x: 60 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="order-1 flex justify-start lg:order-2 lg:justify-center"
        >
          <div className="relative w-full max-w-[8.75rem] sm:max-w-[24rem] lg:max-w-[26rem]">
            <div
              className="absolute -inset-2 rounded-[1.25rem] blur-2xl sm:-inset-4 sm:rounded-[2rem]"
              style={{
                background:
                  "radial-gradient(circle, color-mix(in srgb, var(--accent) 22%, transparent), transparent 60%)",
              }}
            />
            <div
              className="relative overflow-hidden rounded-[1.25rem] border p-2 shadow-[0_22px_50px_rgba(0,0,0,0.24)] backdrop-blur-xl sm:rounded-[2rem] sm:p-3 sm:shadow-[0_32px_90px_rgba(0,0,0,0.34)]"
              style={imageFrameStyle}
            >
              <img
                src={image}
                alt={title}
                className="aspect-[4/5] w-full rounded-[1rem] object-cover sm:rounded-[1.4rem]"
              />
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default SlideSection;
